import { useState } from 'react';
import type { LocJobStatus } from '@/api/play';

/**
 * Progress / summary for the background localization run. State lives in D1, so
 * closing and reopening the tab restores this rather than losing the run.
 */
export function LocJobBanner({ status, onCancel, cancelling }: {
  status: LocJobStatus;
  onCancel: () => void;
  cancelling: boolean;
}) {
  const [showErrors, setShowErrors] = useState(false);
  const job = status.job;
  if (!job) return null;
  const c = status.counts ?? { total: 0, done: 0, failed: 0, pending: 0, running: 0 };
  const active = job.status === 'pending' || job.status === 'running';
  const errors = status.errors ?? [];

  const tone = active
    ? 'border-amber-500/40 bg-amber-500/10'
    : (c.failed > 0 ? 'border-red-500/40 bg-red-500/10' : 'border-green-500/40 bg-green-500/10');

  return (
    <div className={`border rounded-lg p-3 text-sm ${tone}`}>
      <div className="flex flex-wrap items-center gap-2">
        {active ? (
          <span>
            Localizing {job.sourceLang} → <b>{c.done + c.failed}/{c.total}</b>
            {c.failed > 0 && <span className="text-red-400"> · {c.failed} failed</span>}
          </span>
        ) : (
          <span>
            {job.status === 'cancelled' ? 'Localization cancelled' : 'Localization finished'} —
            {' '}<b>{c.done}</b> done{c.failed > 0 && <span className="text-red-400">, {c.failed} failed</span>}.
            {c.done > 0 && ' Review the drafts before submitting.'}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {errors.length > 0 && (
            <button className="text-xs underline text-content-muted" onClick={() => setShowErrors((v) => !v)}>
              {showErrors ? 'Hide errors' : `Show ${errors.length} error(s)`}
            </button>
          )}
          {active && (
            <button className="text-xs underline text-content-muted" disabled={cancelling} onClick={onCancel}>
              {cancelling ? 'Cancelling…' : 'Cancel'}
            </button>
          )}
        </div>
      </div>
      {showErrors && (
        <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto">
          {errors.map((e, i) => (
            <li key={i} className="text-xs text-red-400">
              {e.lang} {e.kind}{e.kind === 'screenshot' ? ` ${e.position + 1}` : ''} — {e.error}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
